import React, { useEffect, useState, useCallback, useRef } from 'react'
import { Outlet } from 'react-router-dom'
import axios from 'axios'
import { updateAdminUserLastLoginApi } from './apis/apis'
import CoreLayout from './CoreLayout'
import 'tailwindcss/tailwind.css'
import './global.css'
import '@fontsource/inter/200.css'
import '@fontsource/inter/400.css'
import '@fontsource/inter/500.css'
import '@fontsource/inter/600.css'
import '@fontsource/inter/800.css'
import { useAuth0 } from '@auth0/auth0-react'
import { getUserPermissions } from './App.slice'
import * as Sentry from '@sentry/react'
import { useAppDispatch } from './shared/redux/hooks'
import TenantSearch from './components/TenantSearch/TenantSearch'
import {
  isTransientAuthError,
  isUsableAccessToken,
  needsInteractiveLogin,
  shouldHydrateUserPermissions,
  sleep,
} from './auth/sessionUtils'

const { VITE_AUTH0_AUDIENCE: AUTH0_AUDIENCE, VITE_AUTH0_SCOPE: AUTH0_SCOPE } = import.meta.env

const App: React.FC = () => {
  const { getAccessTokenSilently, isLoading, isAuthenticated, loginWithRedirect, user } = useAuth0()
  const dispatch = useAppDispatch()

  const [showGlobalSearch, setShowGlobalSearch] = useState(false)
  const [isTokenReady, setIsTokenReady] = useState(false)
  const [authRecoveryNeeded, setAuthRecoveryNeeded] = useState(false)
  const permissionsTokenRef = useRef<string | null>(null)

  useEffect(() => {
    if (!isAuthenticated) {
      setIsTokenReady(false)
      setAuthRecoveryNeeded(false)
      permissionsTokenRef.current = null
    }
  }, [isAuthenticated])

  const applyAccessToken = useCallback(
    (token: string) => {
      localStorage.setItem('accessToken', token)
      axios.defaults.headers.common.Authorization = `Bearer ${token}`
      setIsTokenReady(true)
      setAuthRecoveryNeeded(false)
      if (shouldHydrateUserPermissions(permissionsTokenRef.current, token)) {
        permissionsTokenRef.current = token
        dispatch(getUserPermissions({ accessToken: token, isAuthenticated }))
      }
    },
    [dispatch, isAuthenticated]
  )

  const redirectToLogin = useCallback(() => {
    loginWithRedirect({
      authorizationParams: {
        audience: AUTH0_AUDIENCE,
        scope: AUTH0_SCOPE,
      },
    })
  }, [loginWithRedirect])

  const checkAuthSession = useCallback(async () => {
    const maxAttempts = 3
    let lastError: unknown

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const accessToken = await getAccessTokenSilently({
          detailedResponse: true,
        })
        const token = typeof accessToken === 'object' ? accessToken.access_token : accessToken
        if (typeof token !== 'string' || token.length === 0) {
          throw new Error('Auth0 returned an empty access token')
        }
        applyAccessToken(token)

        try {
          if (user?.email) {
            await updateAdminUserLastLoginApi({
              emailAddress: user?.email,
            })
          }
        } catch (e) {
          console.error('Failed to update admin last login date:', e)
        }
        return
      } catch (error) {
        lastError = error
        if (isTransientAuthError(error) && attempt < maxAttempts) {
          await sleep(500 * attempt)
          continue
        }
        break
      }
    }

    console.error('Auth session error:', lastError)

    const stored = localStorage.getItem('accessToken')
    if (stored != null && isUsableAccessToken(stored)) {
      applyAccessToken(stored)
      return
    }

    if (needsInteractiveLogin(lastError)) {
      redirectToLogin()
      return
    }

    setIsTokenReady(false)
    setAuthRecoveryNeeded(true)
  }, [applyAccessToken, getAccessTokenSilently, redirectToLogin, user?.email])

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.metaKey && event.key === 'k') {
      event.preventDefault()
      setShowGlobalSearch((prev) => !prev)
    }
    if (event.key === 'Escape') {
      setShowGlobalSearch(false)
    }
  }, [])

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      checkAuthSession()
      const intervalId = setInterval(checkAuthSession, 5 * 60 * 1000)
      return () => clearInterval(intervalId)
    }

    if (!isLoading && !isAuthenticated) {
      redirectToLogin()
    }
  }, [isLoading, isAuthenticated, checkAuthSession, redirectToLogin])

  useEffect(() => {
    if (isAuthenticated && user) {
      Sentry.setUser({
        email: user?.email,
      })
      Sentry.setContext('User', {
        email: user?.email,
      })
    }
  }, [isAuthenticated, user])

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleKeyDown])

  if (authRecoveryNeeded) {
    return (
      <div className="px-8 py-6 text-sm text-slate-700 dark:text-slate-300">
        <p className="mb-3">Could not verify your session. Check your connection and try again.</p>
        <div className="flex gap-3">
          <button
            type="button"
            className="rounded bg-slate-800 px-3 py-1.5 text-white dark:bg-slate-200 dark:text-slate-900"
            onClick={() => {
              setAuthRecoveryNeeded(false)
              void checkAuthSession()
            }}
          >
            Retry
          </button>
          <button
            type="button"
            className="rounded border border-slate-300 px-3 py-1.5 dark:border-slate-600"
            onClick={redirectToLogin}
          >
            Sign in
          </button>
        </div>
      </div>
    )
  }

  if (isLoading || !isAuthenticated || !isTokenReady) {
    return <div className="px-8 py-6 text-sm font-medium text-slate-500 dark:text-slate-400 absolute top-0">Loading...</div>
  }

  return (
    <React.Suspense fallback={<div className="px-8 py-6 text-sm font-medium text-slate-500 dark:text-slate-400">Loading...</div>}>
      <CoreLayout>
        <Outlet />
        {showGlobalSearch && (
          <div className="fixed inset-0 z-50 flex justify-center bg-black bg-opacity-50 dark:bg-opacity-70" onClick={() => setShowGlobalSearch(false)}>
            <div className="w-[50%] py-20">
              <TenantSearch setShowGlobalSearch={setShowGlobalSearch} autoFocus />
            </div>
          </div>
        )}
      </CoreLayout>
    </React.Suspense>
  )
}

export default App
